import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OnboardingFieldLabel, OnboardingStepHeader } from '../OnboardingStepHeader';
import { OnboardingSelectionCounter } from '../OnboardingSelectionCounter';
import { OnboardingStepScrollLayout } from '../OnboardingStepScrollLayout';
import { onboardingCheckboxLabelSlotProps } from '../onboardingCheckboxLabelSlotProps';
import { onboardingCheckboxRowSx } from '../onboardingCheckboxRowSx';
import { OnboardingCheckbox } from '../OnboardingCheckbox';
import { useOnboardingLens } from '../onboardingLens';
import {
  getOnboardingMaxTrainingTopicsForPackage,
  ONBOARDING_TRAINING_TOPIC_COLUMNS,
} from '../onboardingConstants';
import type { OnboardingTrainingPackageValue, OnboardingTrainingTopic } from '../OnboardingWizard.types';

interface OnboardingStepTrainingPrioritiesProps {
  trainingPackage: OnboardingTrainingPackageValue | '';
  trainingTopics: OnboardingTrainingTopic[];
  onTrainingTopicsChange: (value: OnboardingTrainingTopic[]) => void;
  showErrors: boolean;
  fieldErrors: {
    trainingTopics?: string;
  };
}

export function OnboardingStepTrainingPriorities({
  trainingPackage,
  trainingTopics,
  onTrainingTopicsChange,
  showErrors,
  fieldErrors,
}: OnboardingStepTrainingPrioritiesProps) {
  const { t } = useTranslation();
  const { layout, tokens } = useOnboardingLens();
  const { formSection, regions: regionsSpec, formField } = layout;

  const maxTrainingTopics = getOnboardingMaxTrainingTopicsForPackage(trainingPackage);
  const selectedCount = trainingTopics.length;
  const atMaxSelection = selectedCount >= maxTrainingTopics;

  const trainingTopicsError =
    showErrors && fieldErrors.trainingTopics
      ? t(`onboarding.wizard.step4.errors.${fieldErrors.trainingTopics}`, {
          max: maxTrainingTopics,
        })
      : undefined;

  const checkboxRowSx = onboardingCheckboxRowSx({
    rowPaddingY: regionsSpec.rowPaddingY,
  });

  const handleTopicToggle = (topic: { id: string; name: string }, checked: boolean) => {
    if (checked) {
      if (atMaxSelection) {
        return;
      }
      onTrainingTopicsChange([...trainingTopics, { id: topic.id, name: topic.name }]);
      return;
    }
    onTrainingTopicsChange(trainingTopics.filter((item) => item.id !== topic.id));
  };

  return (
    <OnboardingStepScrollLayout
      px={formSection.paddingX}
      py={formSection.paddingY}
      headerGap={tokens.core.spacing['0'].value}
      header={
        <OnboardingStepHeader
          title={t('onboarding.wizard.step4.title')}
          subtitle={t('onboarding.wizard.step4.subtitle', { max: maxTrainingTopics })}
          titleSubtitleGap={formSection.headerSubtitleGap}
          subtitleBottomSpacing={formSection.headerSubtitleBottomSpacing}
        />
      }
    >
      <FormControl
        error={Boolean(trainingTopicsError)}
        component="fieldset"
        variant="standard"
        sx={{ width: '100%', m: 0, p: 0, border: 0 }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: formField.labelToInputGap,
            width: '100%',
            minHeight: regionsSpec.labelHeight,
            mb: formField.labelToInputGap,
          }}
        >
          <OnboardingFieldLabel label={t('onboarding.wizard.step4.priorities_label')} />
          <OnboardingSelectionCounter current={selectedCount} max={maxTrainingTopics} />
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: regionsSpec.columnGap,
            width: '100%',
          }}
        >
          {ONBOARDING_TRAINING_TOPIC_COLUMNS.map((column, columnIndex) => (
            <Box
              key={columnIndex === 0 ? 'topics-left' : 'topics-right'}
              sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}
            >
              {column.map((topic) => {
                const isSelected = trainingTopics.some((item) => item.id === topic.id);
                const isDisabled = atMaxSelection && !isSelected;

                return (
                  <FormControlLabel
                    key={topic.id}
                    sx={checkboxRowSx}
                    control={
                      <OnboardingCheckbox
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={(e) => handleTopicToggle(topic, e.target.checked)}
                      />
                    }
                    label={topic.name}
                    slotProps={onboardingCheckboxLabelSlotProps()}
                  />
                );
              })}
            </Box>
          ))}
        </Box>

        {trainingTopicsError ? (
          <FormHelperText error sx={{ mx: 0 }}>
            {trainingTopicsError}
          </FormHelperText>
        ) : null}
      </FormControl>
    </OnboardingStepScrollLayout>
  );
}
